// One row in the "Sent" section — a pending OUTBOUND friend request the
// caller sent. Shows target display_name + a cancel button. Cancel calls
// POST /api/friends/cancel (idempotent — 409 on already-resolved is
// treated identically to 200).
//
// Mirror of FriendRequestRow's chunky-row style; only diff is the action
// (cancel instead of accept/reject) and the labeled identity (target,
// not requester).

import type { components } from '@runegambit/api-types'
import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ApiError } from '../../net/client.js'
import { cancelOutboundFriendRequest } from '../../net/friends.js'
import { Avatar } from '../components/Avatar'

export type OutboundRowEntry = components['schemas']['OutboundFriendRequest']

export interface FriendOutboxRowProps {
  readonly row: OutboundRowEntry
  readonly onCancelled: () => void
}

export function FriendOutboxRow({
  row,
  onCancelled,
}: FriendOutboxRowProps): JSX.Element {
  const [busy, setBusy] = useState(false)

  const handleCancel = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await cancelOutboundFriendRequest(row.request_id)
    } catch (e) {
      // 409 = already resolved — treat as success (the row is gone either
      // way). All other errors are silently swallowed; the next poll will
      // reconcile state.
      if (!(e instanceof ApiError) || e.status !== 409) {
        setBusy(false)
        return
      }
    }
    onCancelled()
  }, [busy, row.request_id, onCancelled])

  const initial = row.target_display_name.slice(0, 1).toUpperCase()

  return (
    <div
      className="row"
      data-testid={`friend-outbox-row-${row.request_id}`}
      data-friend-outbox-row="true"
    >
      <Avatar initial={initial} />
      <span
        style={{
          flex: '1 1 auto',
          fontFamily: 'var(--font-family-body)',
          fontWeight: 500,
          fontSize: 'var(--font-size-16)',
          color: 'var(--color-text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.target_display_name}
      </span>
      <span
        data-testid="friend-outbox-row-pending"
        style={{
          flex: '0 0 auto',
          padding: '4px 8px',
          borderRadius: 'var(--radius-pill)',
          backgroundColor: 'var(--color-paper-2)',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-body)',
          fontWeight: 700,
          fontSize: 'var(--font-size-12)',
          opacity: 0.9,
        }}
      >
        <FormattedMessage
          id="social.friends.outbox.pending"
          defaultMessage="Pending"
        />
      </span>
      <button
        type="button"
        data-cta="friend-outbox-cancel"
        onClick={() => {
          void handleCancel()
        }}
        disabled={busy}
        style={{
          flex: '0 0 auto',
          height: '36px',
          padding: '0 12px',
          backgroundColor: 'transparent',
          color: 'var(--color-text-primary)',
          border: '2px solid var(--color-surface-stroke)',
          borderRadius: '8px',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-13)',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <FormattedMessage
          id="social.friends.outbox.cancel_cta"
          defaultMessage="Cancel"
        />
      </button>
    </div>
  )
}
